import type { IconSvgElement } from "@hugeicons/react";
import {
  DashboardSquare01Icon, Money03Icon, Briefcase01Icon, Building03Icon, Invoice01Icon, Wallet01Icon,
  UserGroupIcon, Calendar01Icon, Shield01Icon, Settings01Icon, ContactBookIcon, Task01Icon, Folder01Icon,
  PackageIcon, File01Icon, Agreement01Icon, CreditCardIcon, DocumentAttachmentIcon, ChartHistogramIcon,
  Building06Icon, ServerStack01Icon, Analytics01Icon, Activity01Icon, Settings02Icon,
} from "@hugeicons/core-free-icons";

export interface NavItem {
  title: string;
  path: string;
  icon: IconSvgElement;
  /** Shown when the user holds any of these permissions in the active organization (empty = everyone, "platform" = platform administrators). */
  permissions: string[];
  /** Active only on this exact path (not on paths below it). */
  exact?: boolean;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

export const SECTIONS: NavSection[] = [
  {
    label: "Main Menu",
    items: [
      { title: "Overview", path: "/", icon: DashboardSquare01Icon, permissions: ["reports.view"] },
      { title: "Leads", path: "/sales/leads", icon: Money03Icon, permissions: ["leads.view"] },
      { title: "Deals", path: "/projects/all", icon: Briefcase01Icon, permissions: ["deals.view"] },
      { title: "Accounts", path: "/clients", icon: Building03Icon, permissions: ["accounts.view"] },
      { title: "Contacts", path: "/contacts", icon: ContactBookIcon, permissions: ["contacts.view"] },
      { title: "Quotes", path: "/quotes", icon: File01Icon, permissions: ["quotes.view"] },
      { title: "Contracts", path: "/contracts", icon: Agreement01Icon, permissions: ["contracts.view"] },
      { title: "Projects", path: "/delivery", icon: Folder01Icon, permissions: ["projects.view"] },
      { title: "Invoices", path: "/invoices", icon: Invoice01Icon, permissions: ["invoices.view"] },
      { title: "Payments", path: "/payments", icon: CreditCardIcon, permissions: ["payments.view"] },
      { title: "Expenses", path: "/expenses", icon: Wallet01Icon, permissions: ["expenses.view"] },
    ],
  },
  {
    label: "Tools",
    items: [
      { title: "Calendar", path: "/calendar", icon: Calendar01Icon, permissions: ["activities.view"] },
      { title: "Tasks", path: "/tasks", icon: Task01Icon, permissions: ["tasks.view"] },
      { title: "Products & Services", path: "/products", icon: PackageIcon, permissions: ["products.view"] },
      { title: "Documents", path: "/documents", icon: DocumentAttachmentIcon, permissions: ["documents.view"] },
      { title: "Sales", path: "/reports", icon: ChartHistogramIcon, permissions: ["reports.view"] },
    ],
  },
  {
    label: "Administration",
    items: [
      { title: "Team", path: "/users", icon: UserGroupIcon, permissions: ["members.view"] },
      { title: "Organization", path: "/organization", icon: Building06Icon, permissions: ["organization.view", "roles.view", "teams.view", "settings.view", "integrations.view", "invoices.configure"] },
      { title: "System Administration", path: "/platform", icon: ServerStack01Icon, permissions: ["platform"] },
      { title: "Audit Logs", path: "/audit-logs", icon: Shield01Icon, permissions: ["audit.view"] },
    ],
  },
];

export const BOTTOM_ITEMS: NavItem[] = [
  { title: "Settings", path: "/settings", icon: Settings01Icon, permissions: [] },
];

/** The system administrator's navigation: the platform only, never an organization's data. */
export const PLATFORM_SECTIONS: NavSection[] = [
  {
    label: "Platform",
    items: [
      { title: "Dashboard", path: "/platform", icon: DashboardSquare01Icon, permissions: [], exact: true },
      { title: "Organizations", path: "/platform/organizations", icon: Building06Icon, permissions: [] },
      { title: "Users", path: "/platform/users", icon: UserGroupIcon, permissions: [] },
      { title: "Analytics", path: "/platform/analytics", icon: Analytics01Icon, permissions: [] },
    ],
  },
  {
    label: "System",
    items: [
      { title: "Activity Log", path: "/platform/activity", icon: Activity01Icon, permissions: [] },
      { title: "System Settings", path: "/platform/settings", icon: Settings02Icon, permissions: [] },
    ],
  },
];

export const PLATFORM_BOTTOM_ITEMS: NavItem[] = [
  { title: "My Account", path: "/settings", icon: Settings01Icon, permissions: [] },
];

export const NAV: NavItem[] = [...SECTIONS.flatMap((sec) => sec.items), ...BOTTOM_ITEMS, ...PLATFORM_SECTIONS.flatMap((sec) => sec.items)];

export const isActiveRoute = (path: string, pathname: string, exact = false) =>
  path === "/" || exact ? pathname === path : pathname === path || pathname.startsWith(path + "/");

export const getNavTitle = (pathname: string) =>
  NAV.find((n) => isActiveRoute(n.path, pathname, n.exact))?.title || "Pelarhive";
